import { useCallback, useEffect, useMemo, useRef } from 'react';

import useWebSocket, { ReadyState } from 'react-use-websocket';

import type {
  Event as EventPB,
  Notification as NotificationPB,
} from '@polyai/stream/protobuf/commands';
import { Envelope } from '@polyai/stream/protobuf/commands';

import { LOGGER_TYPE } from 'api/types';
import { useAppSelector } from 'hooks/reduxHooks';
import useAuthToken from 'hooks/useAuthToken';
import { logToDataDog } from 'lib/ddlogHelper';

export const NOTIFICATION_WS_BASE_URL =
  process.env.NEXT_PUBLIC_PINGU_API || 'ws://localhost:9669';

export interface NotificationSocketParams {
  accountId: string;
  projectId: string;
  branchId: string;
}

export interface UseNotificationSocketOptions {
  onEvent: (event: EventPB) => void;
  onNotification: (notification: NotificationPB) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (event: Event) => void;
}

export function useNotificationSocket(
  { accountId, projectId, branchId }: NotificationSocketParams,
  {
    onEvent,
    onNotification,
    onConnect,
    onDisconnect,
    onError,
  }: UseNotificationSocketOptions,
  isEnabled: boolean,
) {
  const { token, refreshToken } = useAuthToken();

  // Get the current sequence from Redux store
  const lastKnownSequence = useAppSelector(
    state => state.streamTracker.lastKnownSequence,
  );
  const connectionSequence = useRef<
    | {
        branchId: string;
        sequence: number;
      }
    | undefined
  >(undefined);

  const url = useMemo(() => {
    if (accountId && projectId && branchId && lastKnownSequence !== undefined) {
      // We want to keep connecting with the original sequence if we are on the same branch to avoid unnecessary reconnections
      if (
        connectionSequence.current === undefined ||
        connectionSequence.current.branchId !== branchId
      ) {
        connectionSequence.current = {
          branchId,
          sequence: lastKnownSequence,
        };
      }
      return `${NOTIFICATION_WS_BASE_URL}/api/accounts/${accountId}/projects/${projectId}/branches/${branchId}/notifications?sequence=${connectionSequence.current.sequence}`;
    }
    return null;
  }, [accountId, projectId, branchId, lastKnownSequence]);

  const onMessage = useCallback(
    async (msg: MessageEvent<Blob>) => {
      try {
        if (msg.data instanceof Blob) {
          const buffer = await msg.data.arrayBuffer();
          const bytes = new Uint8Array(buffer);
          const envelope = Envelope.decode(bytes);

          if (envelope.message?.$case === 'eventBatch') {
            const events = envelope.message.value.events;
            for (const event of events) {
              void onEvent(event);
            }
          } else if (envelope.message?.$case === 'notification') {
            const notification = envelope.message.value;
            void onNotification(notification);
          }
        }
      } catch (e) {
        console.error('Failed to parse notification message', e, msg.data);
      }
    },
    [onEvent, onNotification],
  );

  const { readyState, sendMessage } = useWebSocket<Blob>(
    url,
    {
      protocols: token ? [token] : undefined,
      onOpen: () => {
        if (onConnect) {
          onConnect();
        }
      },
      onClose: e => {
        // Only reset sequence if we need to reconnect with a different sequence
        // This prevents unnecessary reconnections
        if (onDisconnect) {
          onDisconnect();
        }

        logToDataDog(LOGGER_TYPE.JUPITER, 'warn', 'WebSocket disconnected', {
          error: e,
          status: e.code,
          url,
        });
        void refreshToken();
      },
      onError: event => {
        if (onError) {
          onError(event);
        }
      },
      onMessage: msg => {
        void onMessage(msg);
      },
      share: true,
      shouldReconnect: () => true,
    },
    isEnabled && url !== null && !!token,
  );

  // app level heartbeat to keep intermediaries from timing out idle sockets
  useEffect(() => {
    if (!isEnabled) {
      return;
    }
    let interval: NodeJS.Timeout | null = null;
    if (readyState === ReadyState.OPEN) {
      interval = setInterval(() => {
        try {
          sendMessage('ping');
        } catch {}
      }, 30_000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [readyState, isEnabled, sendMessage]);

  return { readyState };
}
